import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

export type WeighingProcess = 'standard' | 'inverted';

interface WeighingProcessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProcess: (process: WeighingProcess) => void;
  defaultProcess?: WeighingProcess;
}

const WeighingProcessModal: React.FC<WeighingProcessModalProps> = ({
  isOpen,
  onClose,
  onSelectProcess,
  defaultProcess = 'standard'
}) => {
  const [selectedProcess, setSelectedProcess] = useState<WeighingProcess>(defaultProcess);

  const handleConfirm = () => {
    onSelectProcess(selectedProcess);
    onClose();
  };

  const handleCancel = () => {
    setSelectedProcess(defaultProcess);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
        {/* En-tête */}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            Choisir le processus de pesée
          </h3>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Description */}
        <p className="text-gray-600 mb-6">
          Sélectionnez le processus de pesée que vous souhaitez utiliser pour cette nouvelle pesée.
        </p>

        {/* Options de processus */}
        <div className="space-y-4">
          {/* Processus Standard */}
          <div 
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedProcess === 'standard' 
                ? 'border-orange-500 bg-orange-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedProcess('standard')}
          >
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="radio"
                  name="weighingProcess"
                  value="standard"
                  checked={selectedProcess === 'standard'}
                  onChange={() => setSelectedProcess('standard')}
                  className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                />
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Processus Standard
                  </h4>
                  <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                    Recommandé
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Pesée brute (chargé) → Analyse qualité (optionnelle) → Pesée tare (vide) → Ticket
                </p>
                <div className="mt-3 text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span>1. Pesée brute</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                      <span>2. Analyse qualité</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span>3. Pesée tare</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                      <span>4. Ticket</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Processus Inversé */}
          <div 
            className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${
              selectedProcess === 'inverted' 
                ? 'border-orange-500 bg-orange-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => setSelectedProcess('inverted')}
          >
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  type="radio"
                  name="weighingProcess"
                  value="inverted"
                  checked={selectedProcess === 'inverted'}
                  onChange={() => setSelectedProcess('inverted')}
                  className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                />
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center">
                  <h4 className="text-lg font-semibold text-gray-900">
                    Processus Inversé
                  </h4>
                  <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    Spécialisé
                  </span>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  Pesée tare (vide) → Analyse qualité (optionnelle) → Pesée brute (chargé) → Ticket
                </p>
                <div className="mt-3 text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                      <span>1. Pesée tare</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                      <span>2. Analyse qualité</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                      <span>3. Pesée brute</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-purple-500 rounded-full mr-2"></div>
                      <span>4. Ticket</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Informations supplémentaires */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h5 className="font-medium text-gray-900 mb-2">Informations importantes :</h5>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Le processus choisi déterminera l'ordre des étapes de pesée</li>
            <li>• L'analyse qualité peut être ignorée selon le type de produit</li>
            <li>• Vous pourrez changer de processus en cliquant sur "Nouvelle pesée"</li>
          </ul>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className="px-6 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors font-medium"
          >
            Confirmer et commencer
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeighingProcessModal;
